import { useCallback, useRef, useState } from 'react'

const LOAD_MORE_DEBOUNCE_MS = 500

interface UseMessagePaginationNativeOptions {
  /** Callback que faz o fetch de mais mensagens antigas (prepend). */
  onLoadMore: () => Promise<number>
  /** Se ainda há mais mensagens para carregar. */
  hasMore: boolean
}

/**
 * Hook de paginação para MessageList nativa.
 *
 * Diferente do useMessagePagination original, NÃO precisa de
 * firstItemIndex / START_INDEX (artefatos do react-virtuoso pra
 * preservar posição visual em listas virtualizadas reversas).
 * Com scroll nativo, a preservação da posição é feita no componente
 * via useLayoutEffect: mede scrollHeight antes do prepend, ajusta
 * scrollTop depois.
 */
export function useMessagePaginationNative({
  onLoadMore,
  hasMore,
}: UseMessagePaginationNativeOptions) {
  const [isLoading, setIsLoading] = useState(false)
  const isLoadingRef = useRef(false)
  const lastLoadTimeRef = useRef(0)

  /**
   * Tentativa de carregar mais mensagens antigas.
   * Debounced + bloqueia chamadas concorrentes.
   */
  const handleStartReached = useCallback(async () => {
    const now = Date.now()
    if (isLoadingRef.current || !hasMore) return
    if (now - lastLoadTimeRef.current < LOAD_MORE_DEBOUNCE_MS) return

    isLoadingRef.current = true
    lastLoadTimeRef.current = now
    setIsLoading(true)

    try {
      await onLoadMore()
    } finally {
      isLoadingRef.current = false
      setIsLoading(false)
    }
  }, [hasMore, onLoadMore])

  /** Reseta estado (ao trocar de canal). */
  const resetPagination = useCallback(() => {
    setIsLoading(false)
    isLoadingRef.current = false
    lastLoadTimeRef.current = 0
  }, [])

  return {
    isLoading,
    handleStartReached,
    resetPagination,
  }
}
